import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

const NavCtx = createContext({ navContext: null, navigateTo: () => {} });

/** Makes the active tab's nav context reachable from any depth, so a deep
 *  dialog can reopen itself from a dashboard shortcut without prop drilling. */
export const NavProvider = ({ navContext, navigateTo, children }) => {
  const value = useMemo(() => ({ navContext, navigateTo }), [navContext, navigateTo]);
  return <NavCtx.Provider value={value}>{children}</NavCtx.Provider>;
};

export const useNav = () => useContext(NavCtx);

/**
 * Run `handler(value)` once whenever the nav context carries `key`.
 * Tiles navigate with a fresh `_n` stamp, so tapping the same tile twice
 * reopens the record even after the user closed it.
 */
export const useFocusOpen = (key, handler) => {
  const { navContext } = useNav();
  const doneRef = useRef(null);
  const h = useRef(handler);
  h.current = handler;
  useEffect(() => {
    const v = navContext?.[key];
    if (v === undefined || v === null || v === '' || v === false) return;
    const stamp = `${v}|${navContext?._n || ''}`;
    if (doneRef.current === stamp) return;
    doneRef.current = stamp;
    h.current(v);
  }, [navContext, key]);
};

export default NavCtx;
